import React from "react";
import PropTypes from "prop-types";
import GenericCard from "components/UI/GenericCard/GenericCard";
import DashedTooltip from "components/UI/DashedTooltip";

const PASS = "pass";
const WARN = "warn";
const FAIL = "fail";

// Thresholds match the "Apply recommended filters" defaults on the Options
// Leaderboard, so a combo that clears this card is also one that would
// survive that filter.
const SIGNALS_THRESHOLD = 30;
const EDGE_LIFT_THRESHOLD = 1.3;
const PROFIT_FACTOR_THRESHOLD = 1.5;
const TRADABLE_SHARE_THRESHOLD = 0.8;
const STABILITY_DELTA_THRESHOLD = 15;

const CONCENTRATION_THRESHOLD = 50;
const RECENCY_DROP_THRESHOLD = 15;
const MAX_LOSING_STREAK_THRESHOLD = 5;

const buildCriteria = (metrics, isCorrupted) => {
  const {
    completeSignals,
    successRate,
    edge,
    expectancy,
    calendarTradability,
    stability,
    edgeRobustness,
    recentSuccessRate,
  } = metrics;
  const tradableShare = completeSignals
    ? calendarTradability.tradableSignals / completeSignals
    : 0;

  const criteria = [
    {
      label: "Sample size",
      tooltip:
        "Enough signals for the Wilson interval and significance test to actually mean something -- too few and the stats are just noise.",
      severity: "hard",
      status: completeSignals >= SIGNALS_THRESHOLD ? PASS : FAIL,
      value: `${completeSignals} signals (need ≥ ${SIGNALS_THRESHOLD})`,
    },
    {
      label: "Statistically significant edge",
      tooltip:
        "The gap between this signal's success rate and the symbol's own baseline is unlikely to be random chance (p < 0.05).",
      severity: "hard",
      status: edge.isSignificant ? PASS : FAIL,
      value: `p=${edge.pValue} (need < 0.05)`,
    },
    {
      label: "Edge lift",
      tooltip:
        "The signal hits the target meaningfully more often than a random day on this symbol, not just marginally.",
      severity: "hard",
      status: edge.edgeLift !== null && edge.edgeLift >= EDGE_LIFT_THRESHOLD ? PASS : FAIL,
      value:
        edge.edgeLift === null
          ? "N/A"
          : `${edge.edgeLift}x (need ≥ ${EDGE_LIFT_THRESHOLD}x)`,
    },
    {
      label: "Positive expectancy",
      tooltip:
        "Blended average realized return per signal (winners + losers) is positive -- the underlying's move itself is profitable on average, before any option premium/costs.",
      severity: "hard",
      status: expectancy.expectancy > 0 ? PASS : FAIL,
      value: `${expectancy.expectancy}% avg per signal (need > 0)`,
    },
    {
      label: "Profit factor",
      tooltip:
        "Gross gains vs. gross losses across all signals -- ≥ 1.5 means winners meaningfully outweigh losers, not just barely ahead.",
      severity: "soft",
      status:
        expectancy.profitFactor === null
          ? WARN
          : expectancy.profitFactor >= PROFIT_FACTOR_THRESHOLD
            ? PASS
            : WARN,
      value:
        expectancy.profitFactor === null
          ? "N/A (no losses yet)"
          : `${expectancy.profitFactor} (need ≥ ${PROFIT_FACTOR_THRESHOLD})`,
    },
    {
      label: "Calendar tradability",
      tooltip:
        "Share of signals where a real weekly expiry actually gives enough runway to hold to your max bars without being forced out early.",
      severity: "soft",
      status: tradableShare >= TRADABLE_SHARE_THRESHOLD ? PASS : WARN,
      value: `${(tradableShare * 100).toFixed(0)}% tradable (need ≥ ${TRADABLE_SHARE_THRESHOLD * 100}%)`,
    },
    {
      label: "Stable over time",
      tooltip:
        "First-half vs. second-half success rate shouldn't have drifted much -- a big gap suggests the edge may be decaying or was a temporary regime, not a durable pattern.",
      severity: "soft",
      status: Math.abs(stability.delta) <= STABILITY_DELTA_THRESHOLD ? PASS : WARN,
      value: `${stability.firstHalfSuccessRate}% → ${stability.secondHalfSuccessRate}% (Δ${
        stability.delta > 0 ? "+" : ""
      }${stability.delta}pp)`,
    },
    {
      label: "No outsized concentration",
      tooltip:
        "Share of total gain coming from just the top 3 winning signals. High means the edge is riding on a couple of outsized moves rather than being broad-based -- fragile, not durable.",
      severity: "soft",
      status:
        edgeRobustness.topWinnersShare < CONCENTRATION_THRESHOLD
          ? PASS
          : WARN,
      value: `${edgeRobustness.topWinnersShare}% from top 3 (need < ${CONCENTRATION_THRESHOLD}%)`,
    },
    {
      label: "Edge active recently",
      tooltip:
        "Success rate over just the last N signals vs. the all-time rate. An all-time average can look great while the edge has quietly gone stale -- this is the more current read.",
      severity: "soft",
      status:
        successRate - recentSuccessRate.successRate <= RECENCY_DROP_THRESHOLD
          ? PASS
          : WARN,
      value: `${recentSuccessRate.successRate}% over last ${recentSuccessRate.windowSize} (vs ${successRate}% all-time)`,
    },
    {
      label: "Manageable losing streaks",
      tooltip:
        "Longest run of consecutive losses, in chronological order. Even though a single option loss is capped at the premium paid, enough of them back to back is a real risk-of-ruin concern.",
      severity: "soft",
      status:
        edgeRobustness.maxConsecutiveLosses <= MAX_LOSING_STREAK_THRESHOLD
          ? PASS
          : WARN,
      value: `${edgeRobustness.maxConsecutiveLosses} in a row (flagged above ${MAX_LOSING_STREAK_THRESHOLD})`,
    },
  ];

  if (isCorrupted !== undefined) {
    criteria.push({
      label: "Model not trained on this data",
      tooltip:
        "This model was trained on this symbol AND the training window overlaps your requested range -- it's seen the answers it's predicting on, which inflates results.",
      severity: "hard",
      status: isCorrupted ? FAIL : PASS,
      value: isCorrupted ? "Overlaps training data" : "Clean",
    });
  }

  return criteria;
};

const statusClassName = (status) =>
  status === PASS ? "green" : status === FAIL ? "red" : "yellow";

const TradingReadinessCard = (props) => {
  const { optionsResults, isCorrupted } = props;
  const { metrics } = optionsResults;

  const criteria = buildCriteria(metrics, isCorrupted);
  const hardFails = criteria.filter((c) => c.severity === "hard" && c.status === FAIL);
  const softWarns = criteria.filter((c) => c.severity !== "hard" && c.status !== PASS);
  const passCount = criteria.filter((c) => c.status === PASS).length;

  let verdictClass, verdictLabel;
  if (hardFails.length) {
    verdictClass = "red";
    verdictLabel = "Not enough evidence yet";
  } else if (softWarns.length) {
    verdictClass = "yellow";
    verdictLabel = "Caution -- review before trading";
  } else {
    verdictClass = "green";
    verdictLabel = "Statistically ready to trade";
  }

  return (
    <GenericCard
      title="Trading Readiness"
      tooltipText="A checklist of the gates worth clearing before trusting this combo enough to trade real money: enough sample size, a real (statistically significant) edge, positive expectancy, and no obvious cracks in calendar tradability, stability over time, or training overlap."
    >
      <div>
        <h2 className={verdictClass}>{verdictLabel}</h2>
        <h5>
          {passCount}/{criteria.length} checks passed
        </h5>
      </div>
      <div className="pt-2 flex flex-col gap-1">
        {criteria.map((c) => (
          <div key={c.label} className="flex justify-between gap-4">
            <DashedTooltip text={c.tooltip}>
              <span>{c.label}</span>
            </DashedTooltip>
            <span className={statusClassName(c.status)}>{c.value}</span>
          </div>
        ))}
      </div>
    </GenericCard>
  );
};

TradingReadinessCard.propTypes = {
  optionsResults: PropTypes.object,
  isCorrupted: PropTypes.bool,
};

export default TradingReadinessCard;
